import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, ScrollView, TextInput, TouchableOpacity, View } from 'react-native'
import { Ionicons } from '@expo/vector-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'react-native-toast-notifications';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Toggle from '../../../../components/Toggle';
import IconButton from '../../../../components/IconButton';
import Header from '../../../../navigation/Header';
import useCommunitySettings from '../../../../hooks/useCommunitySettings';
import useActiveAdminCommunity from '../../../../hooks/useActiveAdminCommunity';
import useAccessServiceTypes, { AccessServiceType } from '../../../../hooks/useAccessServiceTypes';
import api from '../../../../utils/api';


function ToggleCard({ title, description, value, onValueChange, disabled }: any) {
  return (
    <View style={tw`bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-3`}>
      <View style={tw`row items-center justify-between`}>
        <Text style={tw`text-secondary font-600 text-base flex-1 mr-3`}>{title}</Text>
        <Toggle value={value} onValueChange={onValueChange} disabled={disabled} />
      </View>
      {!!description && <Text style={tw`text-gray-500 text-sm mt-2`}>{description}</Text>}
    </View>
  )
}

/** Card del texto de la responsiva: estado local + botón "Guardar" (no auto-guarda). */
function ResponsibilityTextCard({ value, onSave, disabled, requiresTerms }: { value: string; onSave: (t: string) => void; disabled?: boolean; requiresTerms?: boolean }) {
  const [local, setLocal] = useState(value ?? '')
  useEffect(() => { setLocal(value ?? '') }, [value])

  const dirty = local !== (value ?? '')

  return (
    <View style={tw`bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-3`}>
      <Text style={tw`text-secondary font-600 text-base`}>Texto de responsabilidad</Text>
      <Text style={tw`text-gray-500 text-sm mt-1`}>
        Este texto se mostrará al residente para que lo acepte al solicitar la autorización. Redacta con claridad las condiciones que acepta al autorizar el acceso.
      </Text>
      {!requiresTerms && (
        <Text style={tw`text-amber-600 text-sm mt-2`}>
          El residente no lo verá mientras "Requerir aceptar la responsiva" esté apagado.
        </Text>
      )}

      <TextInput
        value={local}
        onChangeText={setLocal}
        editable={!disabled}
        multiline
        placeholder="Redacta aquí la carta responsiva…"
        placeholderTextColor={tw.color('gray-400')}
        style={tw.style(`bg-gray-100 border border-gray-200 rounded-lg px-4 py-3 text-secondary mt-3`, { minHeight: 130 })}
        textAlignVertical="top"
      />

      <IconButton
        title="Guardar"
        icon={null}
        onPress={() => onSave(local)}
        disabled={disabled || !dirty}
        style={tw.style(`mt-3`, Platform.OS === 'web' ? `self-end` : `self-stretch`)}
      />
    </View>
  )
}


const QR_MODES = [
  { key: 'none', title: 'Sin QR', description: 'El proveedor se presenta en caseta y el vigilante busca la autorización en la lista.' },
  { key: 'guard', title: 'QR en caseta', description: 'El residente comparte un QR que escanea el vigilante. El tótem no lo acepta.' },
  { key: 'guard_totem', title: 'QR en tótem y caseta', description: 'El proveedor puede entrar con el QR en el tótem sin pasar con el vigilante; en caseta también se puede validar.' },
]

const EVIDENCE_ITEMS = [
  { key: 'photos', title: 'Fotos', description: 'El vigilante toma las que indique su consigna: placas, identificaciones, vehículo, carga…' },
  { key: 'signature', title: 'Firma del proveedor' },
]

const EVIDENCE_LEVELS = [
  { key: 'off', label: 'No' },
  { key: 'optional', label: 'Opcional' },
  { key: 'required', label: 'Obligatoria' },
]

const DAYS = [
  { key: 'mon', label: 'Lunes' },
  { key: 'tue', label: 'Martes' },
  { key: 'wed', label: 'Miércoles' },
  { key: 'thu', label: 'Jueves' },
  { key: 'fri', label: 'Viernes' },
  { key: 'sat', label: 'Sábado' },
  { key: 'sun', label: 'Domingo' },
]

const DEFAULT_SERVICE_HOURS: Record<string, { from: string; to: string } | null> = {
  mon: { from: '08:00', to: '18:00' },
  tue: { from: '08:00', to: '18:00' },
  wed: { from: '08:00', to: '18:00' },
  thu: { from: '08:00', to: '18:00' },
  fri: { from: '08:00', to: '18:00' },
  sat: { from: '08:00', to: '14:00' },
  sun: null,
}


function Card({ title, description, children }: any) {
  return (
    <View style={tw`bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-3`}>
      <Text style={tw`text-secondary font-600 text-base`}>{title}</Text>
      {!!description && <Text style={tw`text-gray-500 text-sm mt-1`}>{description}</Text>}
      <View style={tw`mt-3`}>{children}</View>
    </View>
  )
}


function Segmented({ options, value, onChange, disabled }: any) {
  return (
    <View style={tw`flex-row bg-[#F3F2F3] rounded-xl p-1 self-start`}>
      {options.map((o: any) => {
        const active = value === o.key
        return (
          <TouchableOpacity
            key={o.key}
            disabled={disabled}
            onPress={() => !active && onChange(o.key)}
            style={tw.style(`rounded-lg px-3 py-1.5`, active && `bg-white shadow-sm`)}
          >
            <Text style={tw.style(`text-sm`, active ? `text-primary font-600` : `text-gray-500 font-500`)}>{o.label}</Text>
          </TouchableOpacity>
        )
      })}
    </View>
  )
}


/** Cómo se valida en caseta (`access_authorization.qr_mode`). Default de la API: guard_totem. */
function QrModeCard({ value, onChange, disabled }: any) {
  return (
    <Card title="Validación del proveedor" description="Cómo se identifica el proveedor al llegar.">
      {QR_MODES.map((m, i) => {
        const active = value === m.key
        return (
          <TouchableOpacity
            key={m.key}
            disabled={disabled}
            onPress={() => !active && onChange(m.key)}
            style={tw.style(`flex-row py-3`, i > 0 && `border-t border-gray-100`)}
          >
            <View style={tw.style(`w-5 h-5 rounded-full border-2 mr-3 mt-0.5 items-center justify-center`, active ? `border-primary` : `border-gray-300`)}>
              {active && <View style={tw`w-2.5 h-2.5 rounded-full bg-primary`} />}
            </View>
            <View style={tw`flex-1`}>
              <Text style={tw.style(`font-500`, active ? `text-primary` : `text-secondary`)}>{m.title}</Text>
              <Text style={tw`text-gray-500 text-sm mt-0.5`}>{m.description}</Text>
            </View>
          </TouchableOpacity>
        )
      })}
    </Card>
  )
}


/** Evidencia que captura vigilancia al registrar la entrada (`access_authorization.entry_evidence.*`). */
function EntryEvidenceCard({ getSettingValue, updateSettings, disabled }: any) {
  return (
    <Card
      title="Evidencia de entrada"
      description="Lo que el vigilante captura al registrar la entrada del proveedor. Si entra por tótem no se captura evidencia."
    >
      {EVIDENCE_ITEMS.map((item, i) => (
        <View key={item.key} style={tw.style(`py-3`, i > 0 && `border-t border-gray-100`)}>
          <View style={tw`flex-row items-center justify-between flex-wrap gap-2`}>
            <View style={tw`flex-1 min-w-[160px]`}>
              <Text style={tw`text-secondary font-500`}>{item.title}</Text>
              {!!item.description && <Text style={tw`text-gray-500 text-sm`}>{item.description}</Text>}
            </View>
            <Segmented
              options={EVIDENCE_LEVELS}
              value={getSettingValue(`access_authorization.entry_evidence.${item.key}`, 'off')}
              onChange={(v: string) => updateSettings(`access_authorization.entry_evidence.${item.key}`, v)}
              disabled={disabled}
            />
          </View>

        </View>
      ))}
    </Card>
  )
}


/** Alta o edición de un tipo del catálogo, en línea dentro de la tarjeta. */
function ServiceTypeForm({ initial, onSave, onCancel, onDelete, saving }: {
  initial?: AccessServiceType;
  onSave: (data: { name: string; description: string | null }) => void;
  onCancel: () => void;
  onDelete?: () => void;
  saving?: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')

  return (
    <View style={tw`py-3 border-t border-gray-100`}>
      <TextInput
        value={name}
        onChangeText={setName}
        maxLength={80}
        placeholder="Nombre. Ej: Mudanzas y maniobras"
        placeholderTextColor={tw.color('gray-400')}
        style={tw`bg-gray-100 border border-gray-200 rounded-lg px-4 py-2.5 text-secondary`}
      />
      <TextInput
        value={description}
        onChangeText={setDescription}
        maxLength={255}
        multiline
        placeholder="Qué incluye. Ej: Mudanzas, fletes, carga y descarga…"
        placeholderTextColor={tw.color('gray-400')}
        style={tw.style(`bg-gray-100 border border-gray-200 rounded-lg px-4 py-2.5 text-secondary mt-2`, { minHeight: 64 })}
        textAlignVertical="top"
      />

      <View style={tw`row items-center justify-between mt-3`}>
        {onDelete ? (
          <TouchableOpacity onPress={onDelete} disabled={saving}>
            <Text style={tw`text-red text-sm font-500`}>Eliminar</Text>
          </TouchableOpacity>
        ) : <View />}

        <View style={tw`row items-center gap-4`}>
          <TouchableOpacity onPress={onCancel} disabled={saving}>
            <Text style={tw`text-gray-500 text-sm font-500`}>Cancelar</Text>
          </TouchableOpacity>
          <IconButton
            title="Guardar"
            icon={null}
            onPress={() => onSave({ name: name.trim(), description: description.trim() || null })}
            disabled={saving || !name.trim()}
          />
        </View>
      </View>
    </View>
  )
}


/**
 * Catálogo de servicios permitidos (`access_authorization.service_types.enabled` + tabla
 * access_service_types). Al encenderlo por primera vez la API copia los sugeridos. Con catálogo,
 * `access_authorization.description` define si además se pide la descripción libre.
 */
function ServiceTypesCard({ getSettingValue, updateSettings, disabled }: any) {
  const { communityId } = useActiveAdminCommunity()
  const queryClient = useQueryClient()
  const toast = useToast()
  const { types, isLoading } = useAccessServiceTypes(communityId)

  const enabled = !!getSettingValue('access_authorization.service_types.enabled', false)
  const [editing, setEditing] = useState<string | null>(null) // uuid o 'new'
  const [saving, setSaving] = useState(false)

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['access-service-types', communityId] })

  async function toggle(v: boolean) {
    await updateSettings('access_authorization.service_types.enabled', v)
    if (v) refresh()
  }

  async function run(request: () => Promise<any>) {
    setSaving(true)
    try {
      await request()
      await refresh()
      setEditing(null)
    } catch (err: any) {
      const errors = err?.response?.data?.errors
      const first = errors ? (Object.values(errors)[0] as string[])?.[0] : null
      toast.show(first ?? err?.response?.data?.message ?? 'No se pudo guardar', { type: 'warning' })
    } finally {
      setSaving(false)
    }
  }

  const base = `/communities/${communityId}/access-service-types`

  return (
    <View style={tw`bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-3`}>
      <View style={tw`row items-center justify-between`}>
        <Text style={tw`text-secondary font-600 text-base flex-1 mr-3`}>Catálogo de servicios permitidos</Text>
        <Toggle value={enabled} onValueChange={toggle} disabled={disabled} />
      </View>
      <Text style={tw`text-gray-500 text-sm mt-2`}>
        Limita las solicitudes a los tipos de servicio que definas. El residente deberá elegir uno antes de describir el servicio.
      </Text>

      {enabled && (
        <View style={tw`mt-3`}>
          {isLoading && <ActivityIndicator style={tw`my-4`} />}

          {types.map((t) => editing === t.uuid ? (
            <ServiceTypeForm
              key={t.uuid}
              initial={t}
              saving={saving}
              onCancel={() => setEditing(null)}
              onSave={(data) => run(() => api.patch(`${base}/${t.uuid}`, data))}
              onDelete={() => run(() => api.delete(`${base}/${t.uuid}`))}
            />
          ) : (
            <View key={t.uuid} style={tw`row items-start py-3 border-t border-gray-100`}>
              <View style={tw`flex-1 mr-3`}>
                <Text style={tw`text-secondary font-600`}>{t.name}</Text>
                {!!t.description && <Text style={tw`text-gray-500 text-sm mt-0.5`}>{t.description}</Text>}
              </View>
              <TouchableOpacity onPress={() => setEditing(t.uuid)} disabled={saving} accessibilityLabel={`Editar ${t.name}`}>
                <Ionicons name="pencil-outline" size={18} color={tw.color('gray-500')} />
              </TouchableOpacity>
            </View>
          ))}

          {editing === 'new' ? (
            <ServiceTypeForm
              saving={saving}
              onCancel={() => setEditing(null)}
              onSave={(data) => run(() => api.post(base, data))}
            />
          ) : (
            <TouchableOpacity
              onPress={() => setEditing('new')}
              disabled={saving}
              style={tw`row items-center justify-center border border-primary rounded-full py-2.5 mt-3`}
            >
              <Ionicons name="add" size={18} color={tw.color('primary')} />
              <Text style={tw`text-primary font-500 ml-1`}>Agregar tipo de servicio</Text>
            </TouchableOpacity>
          )}

          <View style={tw`row items-center justify-between flex-wrap gap-2 pt-3 mt-4 border-t border-gray-100`}>
            <View style={tw`flex-1 min-w-[160px]`}>
              <Text style={tw`text-secondary font-500`}>Descripción del servicio</Text>
              <Text style={tw`text-gray-500 text-sm`}>Además del tipo, lo que el residente escribe sobre el servicio.</Text>
            </View>
            <Segmented
              options={EVIDENCE_LEVELS}
              value={getSettingValue('access_authorization.description', 'optional')}
              onChange={(v: string) => updateSettings('access_authorization.description', v)}
              disabled={disabled}
            />
          </View>
        </View>
      )}
    </View>
  )
}


const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

/**
 * Horario de servicio para proveedores (`access_authorization.service_hours`). Informativo:
 * el residente lo ve al solicitar y vigilancia ve un aviso fuera de horario.
 */
function ServiceHoursCard({ value, onSave, disabled }: { value: any; onSave: (v: any) => void; disabled?: boolean }) {
  const enabled = !!value
  const [local, setLocal] = useState<any>(value ?? DEFAULT_SERVICE_HOURS)
  useEffect(() => { setLocal(value ?? DEFAULT_SERVICE_HOURS) }, [value])

  const dirty = JSON.stringify(local) !== JSON.stringify(value ?? DEFAULT_SERVICE_HOURS)
  const invalid = DAYS.some(d => local[d.key] && (
    !TIME_RE.test(local[d.key].from) || !TIME_RE.test(local[d.key].to) || local[d.key].from >= local[d.key].to
  ))

  const setDay = (day: string, range: any) => setLocal((prev: any) => ({ ...prev, [day]: range }))

  return (
    <View style={tw`bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-3`}>
      <View style={tw`row items-center justify-between`}>
        <Text style={tw`text-secondary font-600 text-base flex-1 mr-3`}>Horario de servicio</Text>
        <Toggle
          value={enabled}
          onValueChange={(v: boolean) => onSave(v ? DEFAULT_SERVICE_HOURS : null)}
          disabled={disabled}
        />
      </View>
      <Text style={tw`text-gray-500 text-sm mt-2`}>
        Informativo: el residente lo ve al solicitar y el vigilante ve un aviso si el proveedor llega fuera de horario. No bloquea el acceso.
      </Text>

      {enabled && (
        <View style={tw`mt-3`}>
          {DAYS.map((d, i) => {
            const range = local[d.key]
            return (
              <View key={d.key} style={tw.style(`flex-row items-center py-2 flex-wrap gap-2`, i > 0 && `border-t border-gray-100`)}>
                <Text style={tw`text-secondary w-24`}>{d.label}</Text>
                {range ? (
                  <>
                    <TimeInput value={range.from} onChange={(t: string) => setDay(d.key, { ...range, from: t })} disabled={disabled} />
                    <Text style={tw`text-gray-400`}>a</Text>
                    <TimeInput value={range.to} onChange={(t: string) => setDay(d.key, { ...range, to: t })} disabled={disabled} />
                    <TouchableOpacity onPress={() => setDay(d.key, null)} disabled={disabled}>
                      <Text style={tw`text-gray-500 text-sm ml-2`}>Sin servicio</Text>
                    </TouchableOpacity>
                  </>
                ) : (
                  <>
                    <Text style={tw`text-gray-400 flex-1`}>Sin servicio</Text>
                    <TouchableOpacity onPress={() => setDay(d.key, { from: '08:00', to: '18:00' })} disabled={disabled}>
                      <Text style={tw`text-primary text-sm font-500`}>Agregar horario</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            )
          })}

          {invalid && <Text style={tw`text-red text-sm mt-2`}>Revisa los horarios: formato HH:MM y la hora de inicio antes que la de fin.</Text>}

          <IconButton
            title="Guardar horario"
            icon={null}
            onPress={() => onSave(local)}
            disabled={disabled || !dirty || invalid}
            style={tw.style(`mt-3`, Platform.OS === 'web' ? `self-end` : `self-stretch`)}
          />
        </View>
      )}
    </View>
  )
}


function TimeInput({ value, onChange, disabled }: any) {
  return (
    <TextInput
      value={value}
      onChangeText={onChange}
      editable={!disabled}
      placeholder="08:00"
      maxLength={5}
      keyboardType="numbers-and-punctuation"
      style={tw`bg-gray-100 border border-gray-200 rounded-lg px-3 py-1.5 text-secondary w-20 text-center`}
    />
  )
}


// Llaves `access_authorization.*`: las mismas que lee el backend
// (AccessAuthorizationsController, resident/dashboard).
export default function ProviderAuthSettingsScreen() {
  const { updateSettings, getSettingValue, isUpdating } = useCommunitySettings()
  const requiresTerms = getSettingValue('access_authorization.require_terms', false)

  return (
    <View style={tw`flex-1 bg-[#F3F2F3]`}>
      <Header showBackButton title="Autorización a proveedores" style="bg-transparent" />

      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`px-4 pt-4 pb-10`}>
        <Text style={tw`text-gray-500 text-sm mb-4`}>
          Permite a los residentes solicitar el ingreso de proveedores para trabajos mayores, bajo una carta responsiva.
        </Text>

        <ToggleCard
          title="Habilitar autorización de acceso"
          description="Permite a los residentes solicitar el ingreso de proveedores que harán trabajos mayores en su domicilio."
          value={getSettingValue('access_authorization.enabled', false)}
          onValueChange={(v: boolean) => updateSettings('access_authorization.enabled', v)}
          disabled={isUpdating}
        />

        <ToggleCard
          title="Requerir firma de aceptación"
          description="El residente deberá firmar la responsiva para que la solicitud pueda enviarse."
          value={getSettingValue('access_authorization.require_signature', false)}
          onValueChange={(v: boolean) => updateSettings('access_authorization.require_signature', v)}
          disabled={isUpdating}
        />

        <ToggleCard
          title="Requerir aceptar la responsiva"
          description="El residente deberá aceptar el texto de responsabilidad para que la solicitud pueda enviarse."
          value={requiresTerms}
          onValueChange={(v: boolean) => updateSettings('access_authorization.require_terms', v)}
          disabled={isUpdating}
        />

        {/* Siempre editable: el link "Editar responsiva" de la bandeja trae aquí */}
        <ResponsibilityTextCard
          value={getSettingValue('access_authorization.terms', '')}
          onSave={(t) => updateSettings('access_authorization.terms', t)}
          disabled={isUpdating}
          requiresTerms={requiresTerms}
        />

        <ServiceTypesCard getSettingValue={getSettingValue} updateSettings={updateSettings} disabled={isUpdating} />

        <Text style={tw`text-secondary font-600 text-base mt-4 mb-2`}>En caseta</Text>

        <QrModeCard
          value={getSettingValue('access_authorization.qr_mode', 'guard_totem')}
          onChange={(v: string) => updateSettings('access_authorization.qr_mode', v)}
          disabled={isUpdating}
        />

        <EntryEvidenceCard getSettingValue={getSettingValue} updateSettings={updateSettings} disabled={isUpdating} />

        <ServiceHoursCard
          value={getSettingValue('access_authorization.service_hours', null)}
          onSave={(v) => updateSettings('access_authorization.service_hours', v)}
          disabled={isUpdating}
        />
      </ScrollView>
    </View>
  );
}
